/**
 * register.ts — Web Push subscription registration for Sovereign MUSE.
 *
 * Call registerPush() once on app mount. Fully optional and silent: never
 * throws, never blocks the app. Skips cleanly when push isn't supported,
 * the user hasn't logged in yet, the gateway has no VAPID key configured,
 * or the user denies the permission prompt.
 *
 * No preferences UI yet (future slice) — this subscribes the device under
 * the default companion (rook) the first time permission is granted, and
 * remembers the subscribed endpoint in localStorage so later mounts don't
 * re-prompt or re-POST on every load.
 */

import { getStoredToken } from '../hooks/useAuth';

const SUBSCRIBED_ENDPOINT_KEY = 'sovereign_push_endpoint';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function authHeaders(): Record<string, string> {
  const token = getStoredToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export async function registerPush(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    // Mirrors index.html's own service-worker registration skip — no SW is
    // ever installed on localhost there, so `serviceWorker.ready` below
    // would hang forever waiting for a registration that will never arrive.
    if (location.hostname === 'localhost') return;
    if (!getStoredToken()) return; // not authenticated yet — nothing to subscribe as

    const keyRes = await fetch('/api/push/vapid-key', { headers: authHeaders() });
    if (!keyRes.ok) return; // push not configured on this gateway (404) or auth not ready yet
    const { publicKey } = (await keyRes.json()) as { publicKey?: string };
    if (!publicKey) return;

    const registration = await navigator.serviceWorker.ready;

    let subscription = await registration.pushManager.getSubscription();
    if (subscription && localStorage.getItem(SUBSCRIBED_ENDPOINT_KEY) === subscription.endpoint) {
      return; // already subscribed and already told the gateway about it
    }

    if (!subscription) {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        // TS's lib.dom types the Uint8Array backing buffer as ArrayBufferLike
        // (SharedArrayBuffer included), which BufferSource rejects — cast is
        // safe, urlBase64ToUint8Array always allocates a plain ArrayBuffer.
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });
    }

    const res = await fetch('/api/push/subscribe', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ subscription: subscription.toJSON() }),
    });
    if (res.ok) {
      localStorage.setItem(SUBSCRIBED_ENDPOINT_KEY, subscription.endpoint);
    }
  } catch (err) {
    // Never let a push-registration failure disturb the rest of the app.
    console.error('[push] registration failed (non-fatal):', err instanceof Error ? err.message : String(err));
  }
}
